import './SinglePageForm.less';
import React, { ReactNode } from 'react';
import { Form, FormProps, FormInstance } from 'antd';

interface SinglePageFormProps extends Omit<FormProps, 'form'> {
    name?: string;
    onFinish?: (values: any) => void;
    form?: FormInstance;
    children?: ReactNode;
}

interface SubComponentProps {
    children: ReactNode;
}

export default SinglePageForm;

/**
 * @name SinglePageForm
 * @desc Wraps antd form and adds some more functionality like reusable validators and more field decorators.
 */
function SinglePageForm({
    name,
    onFinish,
    form,
    children,
    ...rest
}: SinglePageFormProps) {
    return (
        <Form
            form={form}
            name={name}
            className="SinglePageForm"
            onFinish={onFinish}
            layout="vertical"
            {...rest}
        >
            {children}
        </Form>
    );
}

SinglePageForm.Header = function (props: SubComponentProps) {
    return <div className="SinglePageForm-header">{props.children}</div>;
};

SinglePageForm.Footer = function (props: SubComponentProps) {
    return <div className="SinglePageForm-footer">{props.children}</div>;
};

SinglePageForm.Content = function (props: SubComponentProps) {
    return <div className="SinglePageForm-content">{props.children}</div>;
};

/**
 * Get validation rules for email field
 */
export function getEmailRules() {
    return [
        {
            required: true,
            message: 'Please enter your email.',
        },
        {
            type: 'email' as const,
            message: 'Please enter a valid email.',
        },
    ];
}

/**
 * Get validation rules for password field
 */
export function getPasswordRules(
    validatePassword: boolean = false,
): Array<any> {
    const rules: Array<any> = [
        {
            required: true,
            message: 'Please enter your password.',
        },
    ];

    if (validatePassword) {
        rules.push({
            pattern:
                /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[\W_])[A-Za-z0-9\W_]{8,}$/,
            message: (
                <div style={{ marginTop: '10px' }}>
                    Please enter a valid password.
                </div>
            ),
        });
    }

    return rules;
}

/**
 * Get validation rules for confirm password field
 */
export function getConfirmPasswordRules() {
    return [
        {
            required: true,
            message: 'Please confirm your password.',
        },
        ({ getFieldValue }: any) => ({
            validator(_: any, value: string) {
                if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                }
                return Promise.reject(
                    new Error(
                        'Two passwords that you entered are inconsistent.',
                    ),
                );
            },
        }),
    ];
}

/**
 * Get validation rules for confirm email field
 */
export function getConfirmEmailRules() {
    return [
        {
            required: true,
            message: 'Please confirm your email.',
        },
        {
            type: 'email' as const,
            message: 'Please enter a valid email.',
        },
        ({ getFieldValue }: any) => ({
            validator(_: any, value: string) {
                if (!value || getFieldValue('login') === value) {
                    return Promise.resolve();
                }
                return Promise.reject(
                    new Error('Two emails that you entered are inconsistent.'),
                );
            },
        }),
    ];
}
